import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { BillingStatus, ShopSettings } from '../lib/types.js';
import { AppPage } from '../components/Layout.js';
import { getAppBridge } from '../lib/app-bridge.js';

type SettingsResponse = {
  settings: ShopSettings;
  themeEditor: { appEmbedDeepLink: string };
};

/**
 * Design #10 — Compliance badge settings. Requires the monitoring plan
 * AND the merchant to enable our Theme App Extension's App Embed block in
 * the theme editor (we can configure the extension from here but Shopify
 * won't let us activate it on the merchant's behalf — hence the deep link
 * to the editor).
 */
export function BadgeSettings(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deepLink, setDeepLink] = useState<string | null>(null);

  const [badgeEnabled, setBadgeEnabled] = useState(false);
  const [position, setPosition] = useState<ShopSettings['badgePosition']>('footer');
  const [theme, setTheme] = useState<ShopSettings['badgeTheme']>('auto');
  const [linkEnabled, setLinkEnabled] = useState(true);
  const [shopDomain, setShopDomain] = useState<string | null>(null);
  const [language, setLanguage] = useState<ShopSettings['language']>('en');
  const [compliancePageHandle, setCompliancePageHandle] = useState<string | null>(null);
  const [compliancePageId, setCompliancePageId] = useState<string | null>(null);
  const [compliancePageGeneratedAt, setCompliancePageGeneratedAt] = useState<string | null>(null);

  // Badge text is translation-managed (same reasoning as disclosureText in
  // Settings.tsx) — merchants can't edit it, they switch store language in
  // Settings and the badge reflects that. Computed live from i18n so the
  // preview re-renders when the language round-trips after save.
  const badgeText = t('badge.defaultBadgeTextValue', { lng: language });

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const [s, b] = await Promise.all([
          api.get<SettingsResponse>('/api/settings', ac.signal),
          api.get<BillingStatus>('/api/billing/status', ac.signal),
        ]);
        setBilling(b);
        setBadgeEnabled(s.settings.badgeEnabled);
        setPosition(s.settings.badgePosition || 'footer');
        setTheme(s.settings.badgeTheme || 'auto');
        setLinkEnabled(s.settings.badgeLinkEnabled ?? true);
        setShopDomain(s.settings.shopDomain);
        setLanguage(s.settings.language);
        setCompliancePageHandle(s.settings.compliancePageHandle ?? null);
        setCompliancePageId(s.settings.compliancePageId ?? null);
        setCompliancePageGeneratedAt(s.settings.compliancePageGeneratedAt ?? null);
        setDeepLink(s.themeEditor.appEmbedDeepLink);
      } catch {
        // Non-critical — the form still works with defaults.
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  const isMonitoring = billing?.plan === 'monitoring';
  const canEdit = isMonitoring;

  // Admin-editor URL for the Shopify-hosted compliance page.
  // - Specific page (id present): deep-link to `/admin/pages/<numeric-id>`.
  //   `compliancePageId` is a GraphQL gid (`gid://shopify/OnlineStorePage/123`);
  //   the admin uses the raw numeric id.
  // - No page yet: fall back to the pages listing so the merchant can still
  //   navigate into the admin section (the compliance page will appear there
  //   automatically once the badge is first enabled and the page is auto-created).
  const adminPageEditUrl = (() => {
    if (!shopDomain) return null;
    const base = `https://${shopDomain}/admin/pages`;
    if (!compliancePageId) return base;
    const numericId = compliancePageId.split('/').pop();
    return numericId ? `${base}/${numericId}` : base;
  })();

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const res = await api.put<SettingsResponse & { pageWarning?: string }>('/api/settings', {
        badgeEnabled,
        badgeText,
        badgePosition: position,
        badgeTheme: theme,
        badgeLinkEnabled: linkEnabled,
      });
      setCompliancePageHandle(res.settings.compliancePageHandle ?? null);
      setCompliancePageId(res.settings.compliancePageId ?? null);
      setCompliancePageGeneratedAt(res.settings.compliancePageGeneratedAt ?? null);
      const app = await getAppBridge();
      if (res.pageWarning === 'scope_missing') {
        app.toast.show(t('badge.compliancePageScopeMissing'), { isError: true });
      } else if (res.pageWarning === 'page_create_failed') {
        app.toast.show(t('badge.compliancePageGenerateFailed'), { isError: true });
      } else {
        app.toast.show(t('settings.saved'));
      }
    } catch {
      // Toast is nice-to-have; a hard failure here is rare and surfaces on reload.
    } finally {
      setSaving(false);
    }
  }, [badgeEnabled, badgeText, position, theme, linkEnabled, t]);

  const openThemeEditor = useCallback(() => {
    if (!deepLink) return;
    // Top-level navigation — the theme editor isn't embeddable in our iframe.
    window.open(deepLink, '_top');
  }, [deepLink]);

  if (loading) {
    return (
      <AppPage title={t('badge.title')} backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
      </AppPage>
    );
  }

  return (
    <AppPage
      title={t('badge.title')}
      subtitle={t('badge.subtitle')}
      backAction={{ content: t('plans.back'), onAction: () => navigate('/') }}
      primaryAction={{
        content: t('settings.save'),
        onAction: save,
        loading: saving,
        disabled: !canEdit,
      }}
    >
      <BlockStack gap="400">
        {!isMonitoring && (
          <Banner
            tone="warning"
            title={t('badge.paywallTitle')}
            action={{ content: t('badge.paywallAction'), onAction: () => navigate('/plans') }}
          >
            <p>{t('badge.paywallBody')}</p>
          </Banner>
        )}

        {badgeEnabled && isMonitoring && (
          <Banner tone="success">
            <p>{t('badge.readyBanner')}</p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">{t('badge.previewTitle')}</Text>
            <InlineGrid columns={2} gap="300">
              <PreviewTile
                text={badgeText}
                dark={false}
                label={t('badge.lightTheme')}
                selected={theme === 'light'}
                disabled={!canEdit}
                onSelect={() => canEdit && setTheme('light')}
              />
              <PreviewTile
                text={badgeText}
                dark={true}
                label={t('badge.darkTheme')}
                selected={theme === 'dark'}
                disabled={!canEdit}
                onSelect={() => canEdit && setTheme('dark')}
              />
            </InlineGrid>
            <AutoThemeRow
              selected={theme === 'auto'}
              disabled={!canEdit}
              onSelect={() => canEdit && setTheme('auto')}
              label={t('badge.autoTheme')}
              description={t('badge.adaptsNote')}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{t('badge.settingsTitle')}</Text>
            <ToggleRow
              label={t('badge.showBadge')}
              description={t('badge.showBadgeDesc')}
              enabled={badgeEnabled}
              disabled={!canEdit}
              onChange={setBadgeEnabled}
            />
            <ToggleRow
              label={t('badge.linkBadge')}
              description={t('badge.linkBadgeDesc')}
              enabled={linkEnabled}
              disabled={!canEdit || !badgeEnabled}
              onChange={setLinkEnabled}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{t('badge.textTitle')}</Text>
            <TextField
              label={t('badge.displayText')}
              value={badgeText}
              onChange={() => {}}
              autoComplete="off"
              helpText={
                <span>
                  {t('badge.textReadOnlyHelp')}{' '}
                  <Button
                    variant="plain"
                    onClick={() => navigate('/settings', { state: { scrollTo: 'language' } })}
                  >
                    {t('settings.updateLanguage')}
                  </Button>
                </span>
              }
              disabled
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">{t('badge.placementTitle')}</Text>
            <Select
              label={t('badge.position')}
              options={[
                { label: t('badge.positionFooter'), value: 'footer' },
                { label: t('badge.positionProduct'), value: 'product' },
                { label: t('badge.positionCart'), value: 'cart' },
                { label: t('badge.positionFloating'), value: 'floating' },
              ]}
              value={position}
              onChange={(v) => setPosition(v as ShopSettings['badgePosition'])}
              helpText={t('badge.positionHint')}
              disabled={!canEdit}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">{t('badge.setupTitle')}</Text>
            <Text as="p" tone="subdued">{t('badge.setupIntro')}</Text>
            <SetupStep n={1} body={t('badge.step1')} />
            <SetupStep n={2} body={t('badge.step2')} />
            <SetupStep n={3} body={t('badge.step3')} />
            <InlineStack gap="200">
              <Button onClick={openThemeEditor} disabled={!deepLink} variant="primary">
                {t('badge.openThemeEditor')}
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">{t('badge.compliancePageTitle')}</Text>
            <Text as="p" tone="subdued">{t('badge.compliancePageIntro')}</Text>
            {compliancePageHandle && shopDomain ? (
              <BlockStack gap="100">
                <Text as="p" tone="subdued" variant="bodySm">
                  {t('badge.compliancePageLastGenerated', {
                    when: compliancePageGeneratedAt
                      ? new Date(compliancePageGeneratedAt).toLocaleString()
                      : '—',
                  })}
                </Text>
                <InlineStack gap="200">
                  <Button
                    url={`https://${shopDomain}/pages/${compliancePageHandle}`}
                    external
                    disabled={!canEdit}
                  >
                    {t('badge.compliancePageView')}
                  </Button>
                  {adminPageEditUrl ? (
                    <Button url={adminPageEditUrl} external disabled={!canEdit}>
                      {t('badge.compliancePageEdit')}
                    </Button>
                  ) : null}
                </InlineStack>
              </BlockStack>
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                {t('badge.compliancePageMissing')}{' '}
                {adminPageEditUrl ? (
                  <Button
                    variant="plain"
                    url={adminPageEditUrl}
                    external
                    disabled={!canEdit}
                  >
                    {t('badge.compliancePageEdit')}
                  </Button>
                ) : null}
              </Text>
            )}
          </BlockStack>
        </Card>

        <InlineStack align="end">
          <Button variant="primary" loading={saving} onClick={save} disabled={!canEdit}>
            {t('settings.save')}
          </Button>
        </InlineStack>
      </BlockStack>
    </AppPage>
  );
}

function PreviewTile({
  text,
  dark,
  label,
  selected,
  disabled,
  onSelect,
}: {
  text: string;
  dark: boolean;
  label: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}): JSX.Element {
  // Hover tint and the "active" blue ring both matter for discoverability —
  // users told us the tiles looked like static previews and weren't clickable.
  const [hover, setHover] = useState(false);
  const borderColor = selected ? '#005BD3' : hover && !disabled ? '#B5B7BA' : 'transparent';
  const ringShadow = selected ? '0 0 0 2px rgba(0, 91, 211, 0.2)' : 'none';
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        outline: 'none',
      }}
    >
      <div
        style={{
          background: dark ? '#1a1a1a' : '#F6F6F7',
          borderRadius: 8,
          padding: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${borderColor}`,
          boxShadow: ringShadow,
          transition: 'border-color 0.15s, box-shadow 0.15s',
          position: 'relative',
        }}
      >
        <ShieldBadge text={text} dark={dark} />
        {selected && (
          <div
            aria-hidden
            style={{
              position: 'absolute', top: 6, right: 6,
              width: 18, height: 18, borderRadius: '50%',
              background: '#005BD3', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700, lineHeight: 1,
            }}
          >✓</div>
        )}
      </div>
      <div style={{
        fontSize: 12,
        color: selected ? 'var(--p-color-text)' : 'var(--p-color-text-subdued)',
        fontWeight: selected ? 600 : 400,
        textAlign: 'center',
        marginTop: 6,
      }}>
        {label}
      </div>
    </div>
  );
}

function AutoThemeRow({
  selected,
  disabled,
  onSelect,
  label,
  description,
}: {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  label: string;
  description: string;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  const borderColor = selected ? '#005BD3' : hover && !disabled ? '#B5B7BA' : '#E3E5E7';
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: 12,
        borderRadius: 8,
        border: `2px solid ${borderColor}`,
        boxShadow: selected ? '0 0 0 2px rgba(0, 91, 211, 0.2)' : 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        background: 'var(--p-color-bg-surface)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <div
        aria-hidden
        style={{
          width: 18, height: 18, borderRadius: '50%',
          border: `2px solid ${selected ? '#005BD3' : '#C9CCCF'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected && (
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#005BD3' }} />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', marginTop: 2, lineHeight: 1.5 }}>
          {description}
        </div>
      </div>
    </div>
  );
}

function ShieldBadge({ text, dark }: { text: string; dark: boolean }): JSX.Element {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 7,
      background: dark ? '#2a2a2a' : '#fff',
      border: `1px solid ${dark ? '#3a3a3a' : '#e0e0e0'}`,
      borderRadius: 6, padding: '7px 12px 7px 8px',
    }}>
      <svg width="18" height="22" viewBox="0 0 24 30" fill="none" aria-hidden>
        <path
          d="M12 1L2 5.5V13.5C2 20.5 6.5 26.5 12 28.5C17.5 26.5 22 20.5 22 13.5V5.5L12 1Z"
          fill={dark ? '#1a3a2a' : '#E8F5E9'}
          stroke={dark ? '#4ade80' : '#4CAF50'}
          strokeWidth="1.5"
        />
        <polyline
          points="8 15 11 18 16 12"
          fill="none"
          stroke={dark ? '#86efac' : '#2E7D32'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span style={{ fontSize: 11, fontWeight: 600, color: dark ? '#f0f0f0' : '#1a1a1a' }}>
        {text}
      </span>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  enabled,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  enabled: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 0', borderBottom: '1px solid #F3F4F6',
      opacity: disabled ? 0.55 : 1,
    }}>
      <div style={{ flex: 1, marginRight: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--p-color-text-subdued)', marginTop: 2, lineHeight: 1.5 }}>{description}</div>
      </div>
      <div
        role="switch"
        aria-checked={enabled}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && onChange(!enabled)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onChange(!enabled);
          }
        }}
        style={{
          width: 40, height: 22, borderRadius: 11,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: enabled ? '#005BD3' : '#C9CCCF',
          position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        }}
      >
        <div style={{
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: 2,
          left: enabled ? 20 : 2,
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          transition: 'left 0.2s',
        }} />
      </div>
    </div>
  );
}

function SetupStep({ n, body }: { n: number; body: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{
        width: 22, height: 22, borderRadius: '50%', background: '#DBEAFE', color: '#1E40AF',
        fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {n}
      </div>
      <div
        style={{ fontSize: 13, lineHeight: 1.5 }}
        dangerouslySetInnerHTML={{ __html: body.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
      />
    </div>
  );
}
